// src/pages/healthcare/consultation/consultationComponents/ConsultGuidancePanel.tsx

import React, { useState, useEffect, useCallback } from "react";
import {
	Button, Space, Typography, Tag, Badge, Divider, Card, Spin, Empty,
	Alert, Tooltip, Progress, Input, message, Checkbox,
} from "antd";
import {
	CheckCircleOutlined, ClockCircleOutlined, MessageOutlined, FileTextOutlined,
	BulbOutlined, QuestionCircleOutlined, UserOutlined, ExperimentOutlined,
	WarningOutlined, EditOutlined, SaveOutlined, ReloadOutlined, StarOutlined,
	AlertOutlined, LeftOutlined, RightOutlined, DatabaseOutlined, FormOutlined,
	ThunderboltOutlined, BookOutlined, HeartOutlined, LineChartOutlined, FireOutlined,
	MedicineBoxOutlined, StopOutlined, CalendarOutlined, ArrowRightOutlined, CheckOutlined,
} from "@ant-design/icons";
import { fetchConsultSession, updateConsultSessionNotes, markTopicDiscussed } from "../../../../api/healthcare/Consultation/ConsultApi";
import type { UserUuid } from "../../../../types/identity";

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

// ─── Types ────────────────────────────────────────────────────────────────────

interface ConsultSessionNote {
	id: number;
	step_key: string;
	notes: string | null;
	is_discussed: boolean;
	created_at?: string;
	updated_at?: string;
}

interface ConsultSession {
	id: number;
	appointment_id: number;
	consult_job_id: number;
	patient_id: UserUuid;
	practitioner_id: UserUuid;
	company: string | null;
	session_date: string | null;
	final_guidance: Record<string, any>;
	notes: ConsultSessionNote[];
	created_at: string;
	updated_at: string;
}

interface ConsultGuidancePanelProps {
	sessionId: number | null;
	appointmentId: number;
	isDisabled?: boolean;
	/** Pre-loaded session — skips the internal fetch when provided (e.g. treatment plan context). */
	initialSession?: ConsultSession | null;
}

interface ParsedTopic {
	topic_number?: number;
	title: string;
	section?: string;
	description?: string;
	time_allocation_minutes?: number;
	[key: string]: any;
}

interface TopicSection {
	section_name: string;
	topics: ParsedTopic[];
}

// ─── Utility Functions ────────────────────────────────────────────────────────

const parseTopicsFromGuidance = (guidance: any): ParsedTopic[] => {
	const topics: ParsedTopic[] = [];
	const topicKeys = Object.keys(guidance)
		.filter((key) => /^topic_\d+$/.test(key))
		.sort((a, b) => parseInt(a.replace("topic_", "")) - parseInt(b.replace("topic_", "")));

	topicKeys.forEach((key) => {
		const topicData = guidance[key];
		if (topicData && typeof topicData === "object") {
			topics.push(topicData as ParsedTopic);
		}
	});

	return topics;
};

const inferSection = (topic: ParsedTopic): string => {
	if (topic.section && topic.section !== "Uncategorized") return topic.section;
	// Infer from content fields for peptide workflow
	const num = topic.topic_number ?? 99;
	if (topic.client_profile || topic.top_goals || (num === 1)) return "Patient Assessment";
	if (topic.proceed_decision || topic.flagged_contraindications || (num === 2)) return "Safety Screening";
	if (topic.key_findings || topic.biomarker_summary || (num === 3)) return "Biomarker Analysis";
	if (topic.peptides || (num === 4)) return "Peptide Plan";
	return topic.section || "Consultation";
};

const groupTopicsBySection = (topics: ParsedTopic[]): TopicSection[] => {
	const sectionMap = new Map<string, ParsedTopic[]>();

	topics.forEach((topic) => {
		const sectionName = inferSection(topic);
		if (!sectionMap.has(sectionName)) sectionMap.set(sectionName, []);
		sectionMap.get(sectionName)!.push(topic);
	});

	return Array.from(sectionMap.entries()).map(([section_name, sectionTopics]) => ({
		section_name,
		topics: sectionTopics,
	}));
};

// ─── Content Type Config ──────────────────────────────────────────────────────

const CONTENT_TYPE_CONFIG: Record<string, { icon: React.ReactNode; label: string }> = {
	// Generic
	talking_points:               { icon: <MessageOutlined />,         label: "Talking Points" },
	follow_up_questions:          { icon: <QuestionCircleOutlined />,  label: "Questions" },
	client_context:               { icon: <UserOutlined />,             label: "Client Context" },
	genetic_basis:                { icon: <ExperimentOutlined />,       label: "Genetic Info" },
	practitioner_notes:           { icon: <WarningOutlined />,          label: "Guidance Notes" },
	core_objectives:              { icon: <ThunderboltOutlined />,      label: "Core Objectives" },
	key_intersections:            { icon: <DatabaseOutlined />,         label: "Key Insights" },
	modifiability_table:          { icon: <FormOutlined />,             label: "Modifiability" },
	supplement_review:            { icon: <BookOutlined />,             label: "Supplements" },
	// Blood / lab
	blood_analysis:               { icon: <ExperimentOutlined />,       label: "Blood Analysis" },
	biomarker_analysis:           { icon: <ExperimentOutlined />,       label: "Biomarkers" },
	hormone_panel:                { icon: <HeartOutlined />,            label: "Hormone Panel" },
	hormone_growth_factor_panel:  { icon: <HeartOutlined />,            label: "Hormones & Growth Factors" },
	metabolic_panel:              { icon: <LineChartOutlined />,        label: "Metabolic Panel" },
	metabolic_organ_panel:        { icon: <LineChartOutlined />,        label: "Metabolic & Organ Function" },
	inflammatory_markers:         { icon: <FireOutlined />,             label: "Inflammatory Markers" },
	inflammatory_immune_markers:  { icon: <FireOutlined />,             label: "Inflammation & Immunity" },
	// Peptide patient assessment
	client_profile:               { icon: <UserOutlined />,             label: "Patient Profile" },
	top_goals:                    { icon: <StarOutlined />,             label: "Goals" },
	chief_complaints:             { icon: <AlertOutlined />,            label: "Chief Complaints" },
	// Peptide safety
	proceed_decision:             { icon: <CheckCircleOutlined />,      label: "Eligibility Decision" },
	overall_risk_level:           { icon: <WarningOutlined />,          label: "Risk Level" },
	safety_notes:                 { icon: <WarningOutlined />,          label: "Safety Notes" },
	flagged_contraindications:    { icon: <StopOutlined />,             label: "Contraindications" },
	flagged_interactions:         { icon: <WarningOutlined />,          label: "Drug Interactions" },
	// Peptide biomarker
	key_findings:                 { icon: <ExperimentOutlined />,       label: "Key Biomarker Findings" },
	biomarker_summary:            { icon: <DatabaseOutlined />,         label: "Biomarker Summary" },
	biomarker_targets:            { icon: <DatabaseOutlined />,         label: "Biomarker Targets" },
	// Peptide plan
	peptides:                     { icon: <MedicineBoxOutlined />,      label: "Peptide Protocol" },
	priority_peptides:            { icon: <StarOutlined />,             label: "Priority Peptides" },
	peptide_recommendations:      { icon: <ThunderboltOutlined />,      label: "Peptide Recommendations" },
	secondary_stack:              { icon: <ThunderboltOutlined />,      label: "Secondary Stack" },
	peptide_stack:                { icon: <ThunderboltOutlined />,      label: "Peptide Stack" },
	start_sequence:               { icon: <ArrowRightOutlined />,       label: "Start Sequence" },
	lifestyle_note:               { icon: <HeartOutlined />,            label: "Lifestyle Note" },
	supplement_support:           { icon: <BookOutlined />,             label: "Supplement Support" },
	// Protocol / monitoring
	protocol:                     { icon: <MedicineBoxOutlined />,      label: "Protocol" },
	dosing_protocol:              { icon: <MedicineBoxOutlined />,      label: "Dosing Protocol" },
	peptide_protocols:            { icon: <MedicineBoxOutlined />,      label: "Peptide Protocols" },
	contraindications:            { icon: <StopOutlined />,             label: "Contraindications" },
	safety_contraindications:     { icon: <StopOutlined />,             label: "Safety & Contraindications" },
	safety_assessment:            { icon: <AlertOutlined />,            label: "Safety Assessment" },
	monitoring_plan:              { icon: <CheckCircleOutlined />,      label: "Monitoring Plan" },
	// Summary
	key_points:                   { icon: <BulbOutlined />,             label: "Key Points" },
	before_prescribing:           { icon: <CheckOutlined />,            label: "Before Prescribing" },
	follow_up:                    { icon: <CalendarOutlined />,         label: "Follow-up Plan" },
};

// ─── Specialised Sub-renderers ────────────────────────────────────────────────

const ProceedDecisionBadge: React.FC<{ value: string }> = ({ value }) => {
	const cfg: Record<string, { type: "success" | "warning" | "error" | "info"; label: string }> = {
		proceed:               { type: "success", label: "Proceed — Safe to start peptide therapy" },
		proceed_with_caution:  { type: "warning", label: "Proceed with Caution — Monitor closely" },
		hold:                  { type: "error",   label: "Hold — Resolve concerns before proceeding" },
		no:                    { type: "error",   label: "Do Not Proceed — Peptide therapy contraindicated" },
	};
	const c = cfg[value] ?? { type: "info" as const, label: value };
	return <Alert type={c.type} message={<Text strong style={{ fontSize: 13 }}>{c.label}</Text>} showIcon />;
};

const RiskLevelBadge: React.FC<{ level: string }> = ({ level }) => {
	const colorMap: Record<string, string> = { low: "green", medium: "gold", high: "orange", critical: "red" };
	return (
		<Tag color={colorMap[level?.toLowerCase()] || "default"} style={{ fontSize: 12, padding: "2px 10px", fontWeight: 600 }}>
			Risk: {level?.toUpperCase()}
		</Tag>
	);
};

const PeptidesList: React.FC<{ peptides: any[] }> = ({ peptides }) => (
	<Space direction="vertical" size={12} style={{ width: "100%" }}>
		{peptides.map((p, i) => (
			<Card
				key={i}
				size="small"
				style={{ borderLeft: "3px solid #722ed1" }}
				title={
					<Space style={{ width: "100%", justifyContent: "space-between" }}>
						<Text strong style={{ fontSize: 14, color: "#722ed1" }}>{p.peptide}</Text>
						{p.off_label && <Tag color="orange" style={{ fontSize: 10 }}>Off-label / Investigational</Tag>}
					</Space>
				}
			>
				<Space direction="vertical" size={8} style={{ width: "100%" }}>
					{p.clinical_rationale && (
						<Paragraph style={{ fontSize: 12, marginBottom: 0 }}>{p.clinical_rationale}</Paragraph>
					)}
					<Space wrap size={4}>
						{p.starting_dose && <Tag style={{ fontSize: 11 }}>Dose: {p.starting_dose}</Tag>}
						{p.route && <Tag style={{ fontSize: 11 }}>Route: {p.route}</Tag>}
						{p.timing && <Tag color="blue" style={{ fontSize: 11 }}>{p.timing}</Tag>}
						{p.cycle && <Tag color="purple" style={{ fontSize: 11 }}>Cycle: {p.cycle}</Tag>}
					</Space>
				</Space>
			</Card>
		))}
	</Space>
);

const BiomarkerFindings: React.FC<{ findings: any[] }> = ({ findings }) => {
	const statusColor: Record<string, string> = { low: "blue", high: "orange", critical: "red", borderline: "gold" };
	return (
		<Space direction="vertical" size={8} style={{ width: "100%" }}>
			{findings.map((f, i) => (
				<div key={i} style={{ padding: "10px 12px", background: "var(--mg-bg-subtle)", borderRadius: 6 }}>
					<Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 4 }}>
						<Space size={8}>
							<Text strong style={{ fontSize: 13 }}>{f.marker}</Text>
							{f.value && <Text code style={{ fontSize: 11 }}>{f.value}</Text>}
						</Space>
						{f.status && (
							<Tag color={statusColor[f.status] || "default"} style={{ fontSize: 10, textTransform: "uppercase" }}>
								{f.status}
							</Tag>
						)}
					</Space>
					{f.peptide_implication && (
						<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
							{f.peptide_implication}
						</Text>
					)}
					<Space wrap size={4}>
						{f.supports_peptides?.map((p: string, j: number) => (
							<Tag key={j} color="green" style={{ fontSize: 10 }}>+ {p}</Tag>
						))}
						{f.cautions_peptides?.map((p: string, j: number) => (
							<Tag key={j} color="red" style={{ fontSize: 10 }}>! {p}</Tag>
						))}
					</Space>
				</div>
			))}
		</Space>
	);
};

const ChiefComplaintsList: React.FC<{ complaints: any[] }> = ({ complaints }) => (
	<Space direction="vertical" size={8} style={{ width: "100%" }}>
		{complaints.map((c, i) => (
			<div key={i} style={{ padding: "10px 12px", background: "var(--mg-bg-subtle)", borderRadius: 6 }}>
				<Text strong style={{ fontSize: 13, display: "block", marginBottom: 4 }}>{c.complaint}</Text>
				{c.therapy_domain && (
					<Tag color="blue" style={{ fontSize: 10, marginBottom: 6 }}>{c.therapy_domain}</Tag>
				)}
				{c.peptide_candidates?.length > 0 && (
					<Space wrap size={4}>
						{c.peptide_candidates.map((p: string, j: number) => (
							<Tag key={j} color="purple" style={{ fontSize: 10 }}>{p}</Tag>
						))}
					</Space>
				)}
			</div>
		))}
	</Space>
);

const ContraindicationsList: React.FC<{ items: any[] }> = ({ items }) => {
	const severityType = (s: string): "error" | "warning" | "info" => {
		if (s === "absolute") return "error";
		if (s === "relative") return "warning";
		return "info";
	};
	return (
		<Space direction="vertical" size={8} style={{ width: "100%" }}>
			{items.map((item, i) => (
				<Alert
					key={i}
					type={severityType(item.severity)}
					showIcon={false}
					message={
						<Space style={{ width: "100%", justifyContent: "space-between" }}>
							<Text strong style={{ fontSize: 12 }}>{item.condition}</Text>
							{item.severity && (
								<Tag
									color={item.severity === "absolute" ? "red" : item.severity === "relative" ? "orange" : "gold"}
									style={{ fontSize: 10 }}
								>
									{item.severity}
								</Tag>
							)}
						</Space>
					}
					description={
						item.peptides_affected?.length > 0 ? (
							<Space wrap size={4} style={{ marginTop: 4 }}>
								<Text type="secondary" style={{ fontSize: 10 }}>Affects: </Text>
								{item.peptides_affected.map((p: string, j: number) => (
									<Tag key={j} color="red" style={{ fontSize: 10 }}>{p}</Tag>
								))}
							</Space>
						) : undefined
					}
				/>
			))}
		</Space>
	);
};

const FlaggedInteractionsList: React.FC<{ items: any[] }> = ({ items }) => (
	<Space direction="vertical" size={6} style={{ width: "100%" }}>
		{items.map((item, i) => (
			<Alert
				key={i}
				type="warning"
				showIcon={false}
				message={
					<Space>
						<Text strong style={{ fontSize: 12 }}>{item.medication}</Text>
						{item.peptide_affected && <Tag color="orange" style={{ fontSize: 10 }}>→ {item.peptide_affected}</Tag>}
					</Space>
				}
				description={
					item.interaction ? (
						<Text type="secondary" style={{ fontSize: 11 }}>{item.interaction}</Text>
					) : undefined
				}
			/>
		))}
	</Space>
);

const MonitoringPlanView: React.FC<{ plan: any }> = ({ plan }) => (
	<Space direction="vertical" size={12} style={{ width: "100%" }}>
		{plan.baseline_labs?.length > 0 && (
			<div>
				<Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#666", display: "block", marginBottom: 6 }}>
					Baseline Labs
				</Text>
				<Space direction="vertical" size={2} style={{ width: "100%" }}>
					{plan.baseline_labs.map((lab: string, i: number) => (
						<Space key={i} size={6} align="start">
							<span style={{ fontSize: 9, marginTop: 5 }}>•</span>
							<Text style={{ fontSize: 12 }}>{lab}</Text>
						</Space>
					))}
				</Space>
			</div>
		)}
		{plan.week_4_checkin?.length > 0 && (
			<div>
				<Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#666", display: "block", marginBottom: 6 }}>
					Week 4 Check-in
				</Text>
				<Space direction="vertical" size={2} style={{ width: "100%" }}>
					{plan.week_4_checkin.map((q: string, i: number) => (
						<Space key={i} size={6} align="start">
							<span style={{ fontSize: 9, marginTop: 5 }}>•</span>
							<Text style={{ fontSize: 12 }}>{q}</Text>
						</Space>
					))}
				</Space>
			</div>
		)}
		{plan.week_12_bloods?.length > 0 && (
			<div>
				<Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#666", display: "block", marginBottom: 6 }}>
					Week 12 Bloods
				</Text>
				<Space direction="vertical" size={2} style={{ width: "100%" }}>
					{plan.week_12_bloods.map((lab: string, i: number) => (
						<Space key={i} size={6} align="start">
							<span style={{ fontSize: 9, marginTop: 5 }}>•</span>
							<Text style={{ fontSize: 12 }}>{lab}</Text>
						</Space>
					))}
				</Space>
			</div>
		)}
		{plan.key_targets?.length > 0 && (
			<div>
				<Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#666", display: "block", marginBottom: 6 }}>
					Key Targets
				</Text>
				<Space direction="vertical" size={4} style={{ width: "100%" }}>
					{plan.key_targets.map((t: any, i: number) => (
						<Space key={i} size={8}>
							<Text strong style={{ fontSize: 12 }}>{t.marker}:</Text>
							<Text style={{ fontSize: 12 }}>{t.target}</Text>
						</Space>
					))}
				</Space>
			</div>
		)}
		{plan.stop_criteria?.length > 0 && (
			<div>
				<Text strong style={{ fontSize: 11, textTransform: "uppercase", color: "#cf1322", display: "block", marginBottom: 6 }}>
					Stop Criteria
				</Text>
				<Space direction="vertical" size={4} style={{ width: "100%" }}>
					{plan.stop_criteria.map((c: string, i: number) => (
						<Alert
							key={i}
							type="error"
							showIcon={false}
							message={<Text style={{ fontSize: 12 }}>{c}</Text>}
							style={{ padding: "4px 10px" }}
						/>
					))}
				</Space>
			</div>
		)}
	</Space>
);

const ClientProfileCard: React.FC<{ profile: any }> = ({ profile }) => (
	<div style={{ padding: "12px 14px", background: "var(--mg-bg-subtle)", borderRadius: 8, border: "1px solid var(--border-color)" }}>
		<Space wrap size={16}>
			{profile.name && <Space size={4}><UserOutlined style={{ fontSize: 12, opacity: 0.5 }} /><Text strong style={{ fontSize: 13 }}>{profile.name}</Text></Space>}
			{profile.age && <Space size={4}><ClockCircleOutlined style={{ fontSize: 12, opacity: 0.5 }} /><Text style={{ fontSize: 12 }}>Age: {profile.age}</Text></Space>}
			{profile.sex && <Tag style={{ fontSize: 11 }}>{profile.sex}</Tag>}
			{profile.bmi && <Text style={{ fontSize: 12 }}>BMI: <Text strong>{profile.bmi}</Text></Text>}
		</Space>
		{profile.key_health_context && (
			<Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 6 }}>{profile.key_health_context}</Text>
		)}
	</div>
);

// ─── Smart Value Renderer ─────────────────────────────────────────────────────

const SmartValue: React.FC<{ value: any; compact?: boolean }> = ({ value, compact = false }) => {
	if (value === null || value === undefined || value === "") return null;

	if (typeof value === "boolean") {
		return <Tag color={value ? "success" : "default"} style={{ fontSize: 10 }}>{value ? "Yes" : "No"}</Tag>;
	}

	if (typeof value === "number") {
		return <Text code style={{ fontSize: 13 }}>{value}</Text>;
	}

	if (typeof value === "string") {
		if (value.length < 100 || compact) return <Text style={{ fontSize: 13 }}>{value}</Text>;
		return <Paragraph style={{ fontSize: 13, marginBottom: 0 }}>{value}</Paragraph>;
	}

	if (Array.isArray(value)) {
		if (value.length === 0) return null;
		if (value.every((v) => typeof v !== "object" || v === null)) {
			return (
				<Space direction="vertical" size={4} style={{ width: "100%" }}>
					{value.map((item, i) => (
						<Space key={i} size={4} align="start">
							<span style={{ fontSize: 9, marginTop: 6 }}>•</span>
							<Text style={{ fontSize: 13, flex: 1 }}>{String(item)}</Text>
						</Space>
					))}
				</Space>
			);
		}
		return (
			<Space direction="vertical" size={8} style={{ width: "100%" }}>
				{value.map((item, i) => (
					<Card key={i} size="small" bordered><SmartValue value={item} /></Card>
				))}
			</Space>
		);
	}

	if (typeof value === "object") {
		const entries = Object.entries(value).filter(([, v]) => v !== null && v !== undefined && v !== "");
		if (entries.length === 0) return null;
		return (
			<Space direction="vertical" size={8} style={{ width: "100%" }}>
				{entries.map(([k, v]) => (
					<div key={k}>
						<Text type="secondary" style={{ fontSize: 9, textTransform: "uppercase", display: "block", marginBottom: 2 }}>
							{k.replace(/_/g, " ")}
						</Text>
						<SmartValue value={v} />
					</div>
				))}
			</Space>
		);
	}

	return null;
};

// ─── Risk Badge ───────────────────────────────────────────────────────────────

const RiskBadge: React.FC<{ level?: string }> = ({ level }) => {
	if (!level) return null;
	const colorMap: Record<string, string> = { critical: "red", high: "orange", medium: "gold", low: "green" };
	return (
		<Tag color={colorMap[level.toLowerCase()] || "default"} style={{ fontSize: 9, textTransform: "uppercase", fontWeight: 600 }}>
			{level}
		</Tag>
	);
};

// ─── Content Block Component ──────────────────────────────────────────────────

const ContentBlock: React.FC<{
	type: string;
	data: any;
	defaultExpanded?: boolean;
}> = ({ type, data, defaultExpanded = true }) => {
	const [expanded, setExpanded] = useState(defaultExpanded);
	const config = CONTENT_TYPE_CONFIG[type];

	if (!data || (Array.isArray(data) && data.length === 0)) return null;

	const renderContent = () => {
		// Specialised renderers for peptide fields
		if (type === "proceed_decision" && typeof data === "string") {
			return <ProceedDecisionBadge value={data} />;
		}
		if (type === "overall_risk_level" && typeof data === "string") {
			return <RiskLevelBadge level={data} />;
		}
		if (type === "client_profile" && typeof data === "object" && !Array.isArray(data)) {
			return <ClientProfileCard profile={data} />;
		}
		if (type === "peptides" && Array.isArray(data) && data.length > 0) {
			return <PeptidesList peptides={data} />;
		}
		if (type === "key_findings" && Array.isArray(data) && data.length > 0) {
			return <BiomarkerFindings findings={data} />;
		}
		if (type === "chief_complaints" && Array.isArray(data) && data.length > 0) {
			return <ChiefComplaintsList complaints={data} />;
		}
		if (type === "flagged_contraindications" && Array.isArray(data) && data.length > 0) {
			return <ContraindicationsList items={data} />;
		}
		if (type === "flagged_interactions" && Array.isArray(data) && data.length > 0) {
			return <FlaggedInteractionsList items={data} />;
		}
		if (type === "monitoring_plan" && typeof data === "object" && !Array.isArray(data)) {
			return <MonitoringPlanView plan={data} />;
		}

		// Arrays with badges (numbered)
		if (Array.isArray(data)) {
			return (
				<Space direction="vertical" size={8} style={{ width: "100%" }}>
					{data.map((item, i) => (
						<Space key={i} align="start" size={8}>
							<Badge count={i + 1} showZero style={{ backgroundColor: "#1890ff" }} />
							<div style={{ flex: 1 }}><SmartValue value={item} /></div>
						</Space>
					))}
				</Space>
			);
		}

		return <SmartValue value={data} />;
	};

	return (
		<div>
			<Button
				type="text"
				onClick={() => setExpanded(!expanded)}
				block
				style={{ textAlign: "left", padding: "8px 0", height: "auto" }}
			>
				<Space style={{ width: "100%", justifyContent: "space-between" }}>
					<Space size={8}>
						<span style={{ color: "#1890ff", fontSize: 13 }}>{config?.icon || <FileTextOutlined />}</span>
						<Text strong style={{ fontSize: 13 }}>
							{config?.label || type.replace(/_/g, " ")}
						</Text>
						{Array.isArray(data) && <Badge count={data.length} showZero />}
					</Space>
					<RightOutlined
						style={{
							fontSize: 9, opacity: 0.4,
							transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
							transition: "transform 0.2s",
						}}
					/>
				</Space>
			</Button>
			{expanded && <div style={{ padding: "8px 16px 16px" }}>{renderContent()}</div>}
		</div>
	);
};

// ─── Topic Content Panel ──────────────────────────────────────────────────────

const TopicContent: React.FC<{
	topic: ParsedTopic;
	stepKey: string;
	sessionNote?: ConsultSessionNote;
	onMarkDiscussed: (stepKey: string, discussed: boolean) => void;
	onSaveNotes: (stepKey: string, notes: string) => void;
	isSavingNotes: boolean;
}> = ({ topic, stepKey, sessionNote, onMarkDiscussed, onSaveNotes, isSavingNotes }) => {
	const [notes, setNotes] = useState(sessionNote?.notes || "");
	const notesChanged = notes !== (sessionNote?.notes || "");

	useEffect(() => {
		setNotes(sessionNote?.notes || "");
	}, [stepKey, sessionNote?.notes]);

	// Field classification — order within each group = display order
	const PRIORITY_FIELDS = [
		"talking_points", "follow_up_questions", "core_objectives",
		"top_goals", "chief_complaints",              // peptide T1
		"peptides",                                    // peptide T4
		"key_points", "before_prescribing",            // peptide summary
		"priority_peptides", "peptide_recommendations",
	];
	const CONTEXT_FIELDS = [
		"client_context", "genetic_basis", "key_intersections",
		"client_profile",                              // peptide T1
		"blood_analysis", "biomarker_analysis",
		"hormone_panel", "hormone_growth_factor_panel",
		"metabolic_panel", "metabolic_organ_panel",
		"inflammatory_markers", "inflammatory_immune_markers",
		"key_findings", "biomarker_summary",           // peptide T3
	];
	const GUIDANCE_FIELDS = [
		"practitioner_notes", "modifiability_table", "supplement_review",
		// Safety
		"proceed_decision", "overall_risk_level",      // peptide T2 — promote as first guidance items
		"safety_notes", "flagged_contraindications", "flagged_interactions",
		// Protocols
		"protocol", "dosing_protocol", "peptide_protocols",
		"secondary_stack", "peptide_stack",
		"contraindications", "safety_contraindications", "safety_assessment",
		// Plan support
		"start_sequence", "lifestyle_note", "supplement_support",
		// Monitoring
		"monitoring_plan", "biomarker_targets",
		// Follow-up
		"follow_up",
	];
	const SKIP_FIELDS = new Set([
		"topic_number", "title", "description", "section", "time_allocation_minutes",
		...PRIORITY_FIELDS, ...CONTEXT_FIELDS, ...GUIDANCE_FIELDS,
	]);

	const priorityContent = PRIORITY_FIELDS.filter((k) => topic[k] != null && topic[k] !== "").map((k) => ({ type: k, data: topic[k] }));
	const contextContent  = CONTEXT_FIELDS.filter((k) => topic[k] != null && topic[k] !== "").map((k) => ({ type: k, data: topic[k] }));
	const guidanceContent = GUIDANCE_FIELDS.filter((k) => topic[k] != null && topic[k] !== "").map((k) => ({ type: k, data: topic[k] }));
	const otherContent    = Object.entries(topic)
		.filter(([k, v]) => !SKIP_FIELDS.has(k) && v != null && v !== "" && !(Array.isArray(v) && v.length === 0))
		.map(([k, v]) => ({ type: k, data: v }));

	const isDiscussed = sessionNote?.is_discussed || false;

	// Safety topic: show proceed_decision as a top-level banner
	const proceedDecision = topic.proceed_decision as string | undefined;

	return (
		<div style={{ flex: 1, overflow: "auto" }}>
			<div style={{ maxWidth: 900, margin: "0 auto", padding: 16 }}>
				<Space direction="vertical" size={16} style={{ width: "100%" }}>
					{/* Header */}
					<div>
						<Space direction="vertical" size={12} style={{ width: "100%" }}>
							<Space style={{ width: "100%", justifyContent: "space-between", alignItems: "flex-start" }}>
								<div style={{ flex: 1 }}>
									<Title level={4} style={{ marginBottom: 4, fontWeight: 600 }}>{topic.title}</Title>
									{topic.description && (
										<Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 0 }}>
											{topic.description}
										</Paragraph>
									)}
								</div>
								<Space>
									{topic.time_allocation_minutes && (
										<Tag icon={<ClockCircleOutlined />}>{topic.time_allocation_minutes}m</Tag>
									)}
									{topic.overall_risk_level && <RiskBadge level={topic.overall_risk_level} />}
									{topic.genetic_basis?.risk_level && <RiskBadge level={topic.genetic_basis.risk_level} />}
								</Space>
							</Space>

							{/* Proceed decision banner — shown prominently for safety topic */}
							{proceedDecision && (
								<ProceedDecisionBadge value={proceedDecision} />
							)}

							{/* Discussion Status */}
							<Alert
								message={
									<Space style={{ width: "100%", justifyContent: "space-between" }}>
										<Text strong style={{ fontSize: 13 }}>
											{isDiscussed ? "Discussed \u2713" : "Not yet discussed"}
										</Text>
										<Tooltip title={isDiscussed ? "Unmark as discussed" : "Mark this topic as discussed with the patient"}>
											<Checkbox checked={isDiscussed} onChange={(e) => onMarkDiscussed(stepKey, e.target.checked)}>
												<Text style={{ fontSize: 10 }}>Mark as discussed</Text>
											</Checkbox>
										</Tooltip>
									</Space>
								}
								type={isDiscussed ? "success" : "info"}
								showIcon={false}
								style={{ padding: "8px 12px" }}
							/>
						</Space>
					</div>

					{/* Priority Content — Key Discussion Points */}
					{priorityContent.length > 0 && (
						<Card
							size="small"
							title={
								<Space size={8}>
									<StarOutlined style={{ color: "#1890ff", fontSize: 13 }} />
									<Text strong style={{ fontSize: 13 }}>Key Discussion Points</Text>
								</Space>
							}
							bordered
						>
							<Divider style={{ margin: 0 }} />
							{priorityContent.map((content, i) => (
								<React.Fragment key={i}>
									<ContentBlock type={content.type} data={content.data} defaultExpanded={true} />
									{i < priorityContent.length - 1 && <Divider style={{ margin: 0 }} />}
								</React.Fragment>
							))}
						</Card>
					)}

					{/* Context Content — Background & Context */}
					{contextContent.length > 0 && (
						<Card
							size="small"
							title={
								<Space size={8}>
									<DatabaseOutlined style={{ color: "#722ed1", fontSize: 13 }} />
									<Text strong style={{ fontSize: 13 }}>Background & Context</Text>
								</Space>
							}
							bordered
						>
							<Divider style={{ margin: 0 }} />
							{contextContent.map((content, i) => (
								<React.Fragment key={i}>
									<ContentBlock type={content.type} data={content.data} defaultExpanded={i === 0} />
									{i < contextContent.length - 1 && <Divider style={{ margin: 0 }} />}
								</React.Fragment>
							))}
						</Card>
					)}

					{/* Guidance Content — Clinical Guidance */}
					{/* Filter proceed_decision from guidance since it's already shown as a banner */}
					{guidanceContent.filter(c => c.type !== "proceed_decision").length > 0 && (
						<Card
							size="small"
							title={
								<Space size={8}>
									<BulbOutlined style={{ color: "#faad14", fontSize: 13 }} />
									<Text strong style={{ fontSize: 13 }}>Clinical Guidance</Text>
								</Space>
							}
							bordered
						>
							<Divider style={{ margin: 0 }} />
							{guidanceContent
								.filter(c => c.type !== "proceed_decision")
								.map((content, i, arr) => (
									<React.Fragment key={i}>
										<ContentBlock type={content.type} data={content.data} defaultExpanded={i === 0 && content.type !== "monitoring_plan"} />
										{i < arr.length - 1 && <Divider style={{ margin: 0 }} />}
									</React.Fragment>
								))}
						</Card>
					)}

					{/* Other Content */}
					{otherContent.length > 0 && (
						<Card
							size="small"
							title={
								<Space size={8}>
									<FileTextOutlined style={{ fontSize: 13 }} />
									<Text strong style={{ fontSize: 13 }}>Additional Information</Text>
								</Space>
							}
							bordered
						>
							<Divider style={{ margin: 0 }} />
							{otherContent.map((content, i) => (
								<React.Fragment key={i}>
									<ContentBlock type={content.type} data={content.data} defaultExpanded={false} />
									{i < otherContent.length - 1 && <Divider style={{ margin: 0 }} />}
								</React.Fragment>
							))}
						</Card>
					)}

					{/* Practitioner Notes */}
					<Card
						size="small"
						title={
							<Space size={8}>
								<EditOutlined style={{ color: "#52c41a", fontSize: 13 }} />
								<Text strong style={{ fontSize: 13 }}>Practitioner Notes</Text>
							</Space>
						}
						extra={
							notesChanged ? (
								<Space size={4}>
									<Tooltip title="Discard unsaved changes">
										<Button size="small" onClick={() => setNotes(sessionNote?.notes || "")} disabled={isSavingNotes}>
											Discard Changes
										</Button>
									</Tooltip>
									<Tooltip title="Save notes for this topic">
										<Button
											size="small"
											type="primary"
											loading={isSavingNotes}
											icon={<SaveOutlined />}
											onClick={() => onSaveNotes(stepKey, notes)}
										>
											Save Notes
										</Button>
									</Tooltip>
								</Space>
							) : null
						}
						bordered
					>
						<TextArea
							rows={4}
							placeholder="Add your consultation notes for this topic…"
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							style={{ fontSize: 13, resize: "vertical" }}
						/>
					</Card>
				</Space>
			</div>
		</div>
	);
};

// ─── Executive Summary Panel ──────────────────────────────────────────────────
// Handles both genetic workflow (executive_summary key) and peptide workflow (summary key)

const ExecutiveSummary: React.FC<{ summary: any }> = ({ summary }) => {
	if (!summary) return null;

	// Normalise: both schemas have `overview` and some form of bullet points
	const keyBullets: string[] = summary.key_points ?? summary.key_bullet_points ?? [];
	const nextSteps: string[] = summary.immediate_next_steps ?? [];
	const beforePrescribing: string[] = summary.before_prescribing ?? [];
	const priorityAreas: any[] = summary.priority_focus_areas ?? [];
	const monitoringPlan: any = summary.monitoring_plan;
	const followUp: string = summary.follow_up ?? "";
	const talkingPoints: string[] = summary.talking_points ?? [];

	return (
		<div style={{ flex: 1, overflow: "auto" }}>
			<div style={{ maxWidth: 900, margin: "0 auto", padding: 16 }}>
				<Space direction="vertical" size={16} style={{ width: "100%" }}>
					<div>
						<Title level={4} style={{ marginBottom: 4, fontWeight: 600 }}>
							{summary.title || "Executive Summary"}
						</Title>
						<Paragraph type="secondary" style={{ fontSize: 13 }}>
							High-level overview and key insights from the consultation guidance
						</Paragraph>
					</div>

					{summary.overview && (
						<Card size="small" bordered>
							<Space direction="vertical" size={8} style={{ width: "100%" }}>
								<Text type="secondary" style={{ fontSize: 10, textTransform: "uppercase" }}>Overview</Text>
								<Paragraph style={{ fontSize: 13, marginBottom: 0 }}>{summary.overview}</Paragraph>
							</Space>
						</Card>
					)}

					{keyBullets.length > 0 && (
						<Card
							size="small"
							title={<Space size={8}><BulbOutlined style={{ color: "#faad14", fontSize: 13 }} /><Text strong style={{ fontSize: 13 }}>Key Points</Text></Space>}
							bordered
						>
							<Space direction="vertical" size={8} style={{ width: "100%" }}>
								{keyBullets.map((point: string, i: number) => (
									<Space key={i} align="start" size={8}>
										<Badge count={<StarOutlined style={{ fontSize: 9, color: "#faad14" }} />} />
										<Text style={{ fontSize: 13, flex: 1 }}>{point}</Text>
									</Space>
								))}
							</Space>
						</Card>
					)}

					{beforePrescribing.length > 0 && (
						<Card
							size="small"
							title={<Space size={8}><CheckOutlined style={{ color: "#52c41a", fontSize: 13 }} /><Text strong style={{ fontSize: 13 }}>Before Prescribing</Text></Space>}
							bordered
						>
							<Space direction="vertical" size={10} style={{ width: "100%" }}>
								{beforePrescribing.map((step: string, i: number) => (
									<Space key={i} align="start" size={8}>
										<Badge count={i + 1} showZero style={{ backgroundColor: "#52c41a" }} />
										<Text style={{ fontSize: 13, flex: 1 }}>{step}</Text>
									</Space>
								))}
							</Space>
						</Card>
					)}

					{nextSteps.length > 0 && (
						<Card
							size="small"
							title={<Space size={8}><ThunderboltOutlined style={{ color: "#52c41a", fontSize: 13 }} /><Text strong style={{ fontSize: 13 }}>Immediate Next Steps</Text></Space>}
							bordered
						>
							<Space direction="vertical" size={10} style={{ width: "100%" }}>
								{nextSteps.map((step: string, i: number) => (
									<Space key={i} align="start" size={8}>
										<Badge count={i + 1} showZero style={{ backgroundColor: "#52c41a" }} />
										<Text style={{ fontSize: 13, flex: 1 }}>{step}</Text>
									</Space>
								))}
							</Space>
						</Card>
					)}

					{monitoringPlan && (
						<Card
							size="small"
							title={<Space size={8}><CheckCircleOutlined style={{ color: "#1890ff", fontSize: 13 }} /><Text strong style={{ fontSize: 13 }}>Monitoring Plan</Text></Space>}
							bordered
						>
							<MonitoringPlanView plan={monitoringPlan} />
						</Card>
					)}

					{followUp && (
						<Card size="small" bordered>
							<Space direction="vertical" size={4} style={{ width: "100%" }}>
								<Text type="secondary" style={{ fontSize: 10, textTransform: "uppercase" }}>Follow-up</Text>
								<Space size={8}>
									<CalendarOutlined style={{ color: "#722ed1" }} />
									<Text style={{ fontSize: 13 }}>{followUp}</Text>
								</Space>
							</Space>
						</Card>
					)}

					{priorityAreas.length > 0 && (
						<Card
							size="small"
							title={<Space size={8}><AlertOutlined style={{ color: "#ff4d4f", fontSize: 13 }} /><Text strong style={{ fontSize: 13 }}>Priority Focus Areas</Text></Space>}
							bordered
						>
							<Space direction="vertical" size={10} style={{ width: "100%" }}>
								{priorityAreas.map((area: any, i: number) => (
									<Alert
										key={i}
										type="warning"
										showIcon={false}
										message={<Text strong style={{ fontSize: 13 }}>{area.area}</Text>}
										description={area.rationale && <Text type="secondary" style={{ fontSize: 10 }}>{area.rationale}</Text>}
									/>
								))}
							</Space>
						</Card>
					)}

					{talkingPoints.length > 0 && (
						<Card
							size="small"
							title={<Space size={8}><MessageOutlined style={{ color: "#1890ff", fontSize: 13 }} /><Text strong style={{ fontSize: 13 }}>Closing Talking Points</Text></Space>}
							bordered
						>
							<Space direction="vertical" size={6} style={{ width: "100%" }}>
								{talkingPoints.map((pt: string, i: number) => (
									<Space key={i} size={6} align="start">
										<span style={{ fontSize: 9, marginTop: 5 }}>•</span>
										<Text style={{ fontSize: 13 }}>{pt}</Text>
									</Space>
								))}
							</Space>
						</Card>
					)}
				</Space>
			</div>
		</div>
	);
};

// ─── Left Sidebar Navigation ──────────────────────────────────────────────────

const LeftSidebar: React.FC<{
	sections: TopicSection[];
	sessionNotes: ConsultSessionNote[];
	totalTopics: number;
	discussedCount: number;
	selectedView: "summary" | "topic";
	selectedSectionIdx: number;
	selectedTopicIdx: number;
	onSelectSummary: () => void;
	onSelectTopic: (sectionIdx: number, topicIdx: number) => void;
	progressPct: number;
}> = ({ sections, sessionNotes, totalTopics, discussedCount, selectedView, selectedSectionIdx, selectedTopicIdx, onSelectSummary, onSelectTopic, progressPct }) => (
	<div style={{ width: 256, borderRight: "1px solid var(--border-color)", display: "flex", flexDirection: "column" }}>
		<div style={{ padding: 12, borderBottom: "1px solid var(--border-color)" }}>
			<Space direction="vertical" size={8} style={{ width: "100%" }}>
				<Space size={8}>
					<FileTextOutlined style={{ color: "#722ed1", fontSize: 12 }} />
					<Text strong style={{ fontSize: 13 }}>Consultation Guide</Text>
				</Space>
				<Progress percent={progressPct} size="small" strokeColor="#722ed1" showInfo={false} />
				<Text type="secondary" style={{ fontSize: 11 }}>
					{discussedCount} / {totalTopics} topics discussed
				</Text>
			</Space>
		</div>

		<div style={{ flex: 1, overflow: "auto", padding: 8 }}>
			<Space direction="vertical" size={4} style={{ width: "100%" }}>
				<Button
					type={selectedView === "summary" ? "primary" : "text"}
					onClick={onSelectSummary}
					block
					style={{ textAlign: "left", justifyContent: "flex-start", height: "auto", padding: "8px 12px" }}
					icon={<BookOutlined style={{ fontSize: 13 }} />}
				>
					<Text style={{ fontSize: 13 }}>Summary</Text>
				</Button>

				<Divider style={{ margin: "8px 0" }} />

				{sections.map((section, sIdx) => (
					<div key={sIdx}>
						<div style={{ padding: "8px 12px" }}>
							<Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
								{section.section_name}
							</Text>
						</div>
						<Space direction="vertical" size={4} style={{ width: "100%" }}>
							{section.topics.map((topic, tIdx) => {
								const topicStepKey = `topic_${topic.topic_number}`;
								const isDiscussed = sessionNotes.find((n) => n.step_key === topicStepKey)?.is_discussed || false;
								const isActive = selectedView === "topic" && selectedSectionIdx === sIdx && selectedTopicIdx === tIdx;

								return (
									<Button
										key={tIdx}
										type={isActive ? "primary" : isDiscussed ? "default" : "text"}
										onClick={() => onSelectTopic(sIdx, tIdx)}
										block
										style={{
											textAlign: "left",
											justifyContent: "flex-start",
											height: "auto",
											padding: "8px 12px",
											...(isDiscussed && !isActive ? { borderColor: "#52c41a", color: "#52c41a" } : {}),
										}}
									>
										<Space style={{ width: "100%", justifyContent: "space-between" }}>
											<Space size={8} style={{ flex: 1, minWidth: 0 }}>
												{isDiscussed ? (
													<CheckCircleOutlined style={{ color: isActive ? "#fff" : "#52c41a", fontSize: 10 }} />
												) : (
													<span
														style={{
															display: "inline-block", width: 10, height: 10, borderRadius: "50%",
															border: `2px solid ${isActive ? "#fff" : "var(--border-color)"}`,
														}}
													/>
												)}
												<Text
													ellipsis
													style={{ fontSize: 11, color: isActive ? "#fff" : isDiscussed ? "#52c41a" : "inherit", flex: 1 }}
												>
													{topic.title}
												</Text>
											</Space>
											{topic.time_allocation_minutes && (
												<Text type="secondary" style={{ fontSize: 9, opacity: 0.6 }}>
													{topic.time_allocation_minutes}m
												</Text>
											)}
										</Space>
									</Button>
								);
							})}
						</Space>
					</div>
				))}
			</Space>
		</div>
	</div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ConsultGuidancePanel: React.FC<ConsultGuidancePanelProps> = ({ sessionId, appointmentId, isDisabled, initialSession }) => {
	const [session, setSession] = useState<ConsultSession | null>(initialSession ?? null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [selectedView, setSelectedView] = useState<"summary" | "topic">("summary");
	const [selectedSectionIdx, setSelectedSectionIdx] = useState(0);
	const [selectedTopicIdx, setSelectedTopicIdx] = useState(0);
	const [isSavingNotes, setIsSavingNotes] = useState(false);

	const loadSession = useCallback(async () => {
		if (initialSession !== undefined) return;
		if (!appointmentId) return;
		setLoading(true);
		setError(null);
		try {
			const res = await fetchConsultSession(appointmentId);
			setSession(res.session ? { ...res.session, notes: res.session.notes || [] } : null);
		} catch (e: any) {
			setError(e?.response?.data?.message || "Failed to load consultation guidance");
		} finally {
			setLoading(false);
		}
	}, [appointmentId, initialSession]);

	useEffect(() => { loadSession(); }, [loadSession]);

	const handleMarkDiscussed = useCallback(
		async (stepKey: string, discussed: boolean) => {
			if (!session) return;
			try {
				await markTopicDiscussed(session.id, stepKey, discussed);
				setSession((prev) => {
					if (!prev) return prev;
					const hasNote = prev.notes.some((n) => n.step_key === stepKey);
					return {
						...prev,
						notes: hasNote
							? prev.notes.map((n) => (n.step_key === stepKey ? { ...n, is_discussed: discussed } : n))
							: [...prev.notes, { id: 0, step_key: stepKey, notes: null, is_discussed: discussed }],
					};
				});
			} catch {
				message.error("Failed to update topic status");
			}
		},
		[session],
	);

	const handleSaveNotes = useCallback(
		async (stepKey: string, noteText: string) => {
			if (!session) return;
			setIsSavingNotes(true);
			try {
				await updateConsultSessionNotes(session.id, stepKey, noteText);
				setSession((prev) => {
					if (!prev) return prev;
					const hasNote = prev.notes.some((n) => n.step_key === stepKey);
					return {
						...prev,
						notes: hasNote
							? prev.notes.map((n) => (n.step_key === stepKey ? { ...n, notes: noteText } : n))
							: [...prev.notes, { id: 0, step_key: stepKey, notes: noteText, is_discussed: false }],
					};
				});
				message.success("Notes saved", 1.5);
			} catch {
				message.error("Failed to save notes");
			} finally {
				setIsSavingNotes(false);
			}
		},
		[session],
	);

	const navigateNext = useCallback(() => {
		if (!session) return;
		const sections = groupTopicsBySection(parseTopicsFromGuidance(session.final_guidance));
		const current = sections[selectedSectionIdx];
		if (selectedTopicIdx < current.topics.length - 1) {
			setSelectedTopicIdx((p) => p + 1);
		} else if (selectedSectionIdx < sections.length - 1) {
			setSelectedSectionIdx((p) => p + 1);
			setSelectedTopicIdx(0);
		}
	}, [session, selectedSectionIdx, selectedTopicIdx]);

	const navigatePrev = useCallback(() => {
		if (!session) return;
		const sections = groupTopicsBySection(parseTopicsFromGuidance(session.final_guidance));
		if (selectedTopicIdx > 0) {
			setSelectedTopicIdx((p) => p - 1);
		} else if (selectedSectionIdx > 0) {
			const prev = sections[selectedSectionIdx - 1];
			setSelectedSectionIdx((p) => p - 1);
			setSelectedTopicIdx(prev.topics.length - 1);
		}
	}, [session, selectedSectionIdx, selectedTopicIdx]);

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "ArrowRight" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); navigateNext(); }
			if (e.key === "ArrowLeft"  && (e.metaKey || e.ctrlKey)) { e.preventDefault(); navigatePrev(); }
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [navigateNext, navigatePrev]);

	if (!appointmentId && !initialSession) {
		return (
			<div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={
						<Space direction="vertical" size={4}>
							<Text style={{ fontSize: 13 }}>No consultation guidance available</Text>
							<Text type="secondary" style={{ fontSize: 10 }}>Generate guidance from appointment details</Text>
						</Space>
					}
				/>
			</div>
		);
	}

	if (loading) {
		return (
			<div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
				<Space direction="vertical" align="center">
					<Spin />
					<Text type="secondary" style={{ fontSize: 13 }}>Loading guidance...</Text>
				</Space>
			</div>
		);
	}

	if (error) {
		return (
			<div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
				<Alert
					type="error"
					message="Failed to load guidance"
					description={<Text style={{ fontSize: 10 }}>{error}</Text>}
					action={<Button size="small" icon={<ReloadOutlined />} onClick={loadSession}>Retry</Button>}
				/>
			</div>
		);
	}

	if (!session) return null;

	const guidance       = session.final_guidance;
	const parsedTopics   = parseTopicsFromGuidance(guidance);
	const sections       = groupTopicsBySection(parsedTopics);
	const currentSection = sections[selectedSectionIdx];
	const currentTopic   = currentSection?.topics?.[selectedTopicIdx];
	const currentStepKey = currentTopic?.topic_number ? `topic_${currentTopic.topic_number}` : undefined;
	const currentNote    = currentStepKey ? session.notes.find((n) => n.step_key === currentStepKey) : undefined;

	// Support both genetic workflow (executive_summary key) and peptide workflow (summary key)
	const summaryData    = guidance?.executive_summary ?? guidance?.summary;

	const totalTopics    = parsedTopics.length;
	const discussedCount = session.notes.filter((n) => n.is_discussed).length;
	const progressPct    = totalTopics > 0 ? Math.round((discussedCount / totalTopics) * 100) : 0;

	const canGoPrev = selectedView === "topic" && (selectedTopicIdx > 0 || selectedSectionIdx > 0);
	const canGoNext = selectedView === "topic" && currentSection
		&& (selectedTopicIdx < currentSection.topics.length - 1 || selectedSectionIdx < sections.length - 1);

	return (
		<div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
				<LeftSidebar
					sections={sections}
					sessionNotes={session.notes}
					totalTopics={totalTopics}
					discussedCount={discussedCount}
					selectedView={selectedView}
					selectedSectionIdx={selectedSectionIdx}
					selectedTopicIdx={selectedTopicIdx}
					onSelectSummary={() => setSelectedView("summary")}
					onSelectTopic={(sIdx, tIdx) => {
						setSelectedView("topic");
						setSelectedSectionIdx(sIdx);
						setSelectedTopicIdx(tIdx);
					}}
					progressPct={progressPct}
				/>

				<div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
					{selectedView === "summary" && <ExecutiveSummary summary={summaryData} />}
					{selectedView === "topic" && currentTopic && currentStepKey && (
						<TopicContent
							topic={currentTopic}
							stepKey={currentStepKey}
							sessionNote={currentNote}
							onMarkDiscussed={handleMarkDiscussed}
							onSaveNotes={handleSaveNotes}
							isSavingNotes={isSavingNotes}
						/>
					)}

					{selectedView === "topic" && (
						<div style={{ borderTop: "1px solid var(--border-color)", padding: 12 }}>
							<div style={{ maxWidth: 900, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
								<Tooltip title="Go to previous topic (Ctrl/Cmd + ←)">
									<Button size="small" icon={<LeftOutlined />} disabled={!canGoPrev} onClick={navigatePrev}>
										Previous Topic
									</Button>
								</Tooltip>

								<Space size={8}>
									<Text type="secondary" style={{ fontSize: 10 }}>
										{selectedTopicIdx + 1} / {currentSection?.topics.length || 0}
									</Text>
									<Divider type="vertical" style={{ margin: 0 }} />
									<Text type="secondary" style={{ fontSize: 9 }}>Ctrl/Cmd + Arrow keys</Text>
								</Space>

								<Tooltip title="Go to next topic (Ctrl/Cmd + →)">
									<Button size="small" type="primary" disabled={!canGoNext} onClick={navigateNext}>
										Next Topic <RightOutlined />
									</Button>
								</Tooltip>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default ConsultGuidancePanel;
